import * as React from 'react';
import { cloneDeep } from 'lodash';
import styled from 'styled-components';
import { AgentsContext, OrgBasicUser } from 'contexts';
import { buildFullName } from 'utils';
import { Icon, theme, Avatar } from 'components';
import { UserLicenseType, UserRoles } from '@bb/users';
import { AgentsContextUser } from 'contexts/AgentsContext';

import type { OrgBasicFilterType } from '@/app-package';
import { Select, SelectProps, SelectOption } from '../Select';
import { Contact } from '../Contact';

interface UserSelectProps extends Omit<SelectProps, 'options' | 'onChange'> {
  locations?: string[];
  filter?: (option: OrgBasicUser) => boolean;
  backEndFilter?: OrgBasicFilterType;
  targetUserIds?: string[];
  teamMembers?: AgentsContextUser[];
  selectAllAvailable?: boolean;
  onChange: (ids: string[], users: OrgBasicUser[]) => void;
  sorted?: boolean;
  suppressInitialFetch?: boolean;
  withSearchIcon?: boolean;
  orgWide?: boolean;
  disabled?: boolean;
}

const UserSelect: React.FC<UserSelectProps> = ({
  locations,
  filter = () => true,
  backEndFilter,
  targetUserIds,
  mode,
  teamMembers,
  value,
  selectAllAvailable,
  onChange,
  sorted,
  suppressInitialFetch,
  withSearchIcon,
  orgWide,
  disabled,
  ...props
}) => {
  const { orgUsers, orgUsersMap, fetchOrgUsers } =
    React.useContext(AgentsContext);

  React.useEffect(() => {
    if (!suppressInitialFetch)
      fetchOrgUsers(backEndFilter, targetUserIds, orgWide);
  }, [backEndFilter, suppressInitialFetch, targetUserIds]);

  const options = React.useMemo(() => {
    const queryLocations = new Set(locations);
    const users = (cloneDeep(teamMembers) || cloneDeep(orgUsers))
      // include mls id on the label so it can be searched
      .map(user => {
        const mlsNumbers =
          user.mls_numbers
            ?.map(({ number, board }) => `${number}${board}`)
            .join('') || '';
        return { ...user, label: `${user.label} ${mlsNumbers}` };
      });
    const userOptions = !locations
      ? users
      : users.filter(({ location }) => queryLocations.has(location));
    if (selectAllAvailable) {
      userOptions.unshift({
        id: 'ALL',
        label: 'SEARCH ENTIRE BROKERAGE',
        firstName: 'SEARCH ENTIRE BROKERAGE',
        lastName: '',
        addressName: '',
        location: '-',
        licenseType: UserLicenseType.UNLICENSED,
        role: UserRoles.Agent,
        mls_numbers: [],
      });
    }
    if (sorted) {
      userOptions.sort((a, b) =>
        `${buildFullName(a.firstName, a.lastName)}`
          .toLowerCase()
          .localeCompare(
            `${buildFullName(b.firstName, b.lastName)}`.toLowerCase(),
          ),
      );
    }
    return userOptions;
  }, [orgUsers, locations, selectAllAvailable, sorted, teamMembers]);

  const handleChange = (id: string[] | string) => {
    if (!id) return onChange(undefined, undefined);

    const ids = typeof id === 'string' ? [id] : id;
    const user: OrgBasicUser[] = teamMembers
      ? teamMembers.filter(member => ids.includes(member.id))
      : ids.map(currId => orgUsersMap![currId]);

    onChange(ids, user);
  };

  return (
    <>
      {withSearchIcon &&
        (value ? (
          <StyledAvatar
            user={orgUsers.find(orgUser => orgUser.id === value[0])}
            width={16}
          />
        ) : (
          <StyledIcon type="search" style={{ color: theme.alpha85 }} />
        ))}
      <StyledSelect
        optionLabelProp="label"
        optionFilterProp="label"
        mode={mode}
        value={value}
        onChange={handleChange}
        withSearchIcon={withSearchIcon}
        disabled={disabled}
        {...props}
      >
        {options.filter(filter).map(user => (
          <SelectOption key={user.id} value={user.id} label={user.label}>
            <Contact
              contact={user}
              avatarSize={32}
              showBranchName
              disableClick
              showMlsIds
            />
          </SelectOption>
        ))}
      </StyledSelect>
    </>
  );
};

const StyledIcon = styled(Icon)`
  position: absolute;
  top: 213px;
  left: 30px;
  z-index: 10001;
`;

const StyledAvatar = styled(Avatar)`
  position: absolute;
  top: 213px;
  left: 30px;
  z-index: 10001;
`;

const StyledSelect = styled(Select)<{ withSearchIcon: boolean }>`
  ${({ withSearchIcon }) =>
    withSearchIcon &&
    `
  .ant-select-selection-placeholder,
  .ant-select-selection-search,
  .ant-select-selection-item {
    margin-left: 18px;
  }
  `}
`;

export default UserSelect;
